/**
 * ProviderSections — renders the 7 collapsible sections inside UnifiedProviderCard.
 * Split out to keep each file under the 500-line budget.
 */
import React, { useCallback, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import type { ProviderInfo, LicenseEntry } from '../../types';
import TaxonomyCodeField from '../../../../components/shared/TaxonomyCodeField';
import IsoDateField from '../../../../components/shared/IsoDateField';
import {
  BILLING_TIER_ELIGIBLE_ROLES_SET,
  BILLING_BAKED_IN_ROLES,
} from '../../../../utils/permissionConstants';
import {
  BILLING_TIER_OPTIONS,
  SUPERBILL_NPI_OPTIONS,
  TELEHEALTH_MODE_OPTIONS,
  US_STATES,
} from './providerCardConstants';
import type { DataSource, RoleOption, SelectOption } from './providerCardHelpers';
import { CRAWL_FIELDS, hasValue } from './providerCardHelpers';
import { formatLicenseTypeLabel } from '../../../../constants/licenseTypes';

/* ─── Crawl highlight wrapper ─── */

function HL({ field, provider, ds, children }: {
  field: string; provider: ProviderInfo; ds: DataSource; children: React.ReactNode;
}) {
  const show = ds === 'crawl' && CRAWL_FIELDS.has(field) && hasValue((provider as unknown as Record<string, unknown>)[field]);
  return (
    <Box sx={show ? { borderLeft: '3px solid', borderColor: 'info.main', pl: 1, borderRadius: 0.5 } : undefined}>
      {children}
    </Box>
  );
}

/* ─── Props shared by all section renderers ─── */

export interface SectionProps {
  provider: ProviderInfo;
  idx: number;
  ds: DataSource;
  onUpdate: (idx: number, field: string, value: unknown) => void;
  roleOptions: RoleOption[];
  licenseTypes: string[];
  degreeTypes: string[];
  payTypes: SelectOption[];
  timesheetModes: SelectOption[];
  allProviderNames: string[];
  showPayType: boolean;
  getRoleLabel: (role: string) => string;
}

/* ─── Convenience text field change handler ─── */
function useFieldChange(idx: number, onUpdate: SectionProps['onUpdate']) {
  return useCallback(
    (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onUpdate(idx, field, e.target.value),
    [idx, onUpdate],
  );
}

/* ─── Section 1: Identity ─── */

export function IdentitySection({ provider, idx, ds, onUpdate }: SectionProps) {
  const u = useFieldChange(idx, onUpdate);
  return <>
    <Grid size={{ xs: 12, sm: 4 }}>
      <HL field="first_name" provider={provider} ds={ds}>
        <TextField label="First Name" value={provider.first_name} onChange={u('first_name')} size="small" required fullWidth helperText="Legal name — used on records and billing" />
      </HL>
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <HL field="last_name" provider={provider} ds={ds}>
        <TextField label="Last Name" value={provider.last_name} onChange={u('last_name')} size="small" required fullWidth helperText="Legal name — used on records and billing" />
      </HL>
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField label="Preferred name (used in greetings)" value={provider.preferred_name || ''} onChange={u('preferred_name')} size="small" fullWidth
        helperText="What this provider likes to be called" />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <HL field="title" provider={provider} ds={ds}>
        <TextField label="Job Title" value={provider.title ?? ''} onChange={u('title')} size="small" fullWidth
          helperText="Position title, e.g. Clinical Associate" placeholder="e.g. Clinical Associate" />
      </HL>
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField label="Email" type="email" value={provider.email} onChange={u('email')} size="small" fullWidth
        placeholder="email@example.com" helperText="Invite sent on wizard completion" />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField label="Direct Phone" value={provider.direct_phone ?? ''} onChange={u('direct_phone')} size="small" fullWidth />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <HL field="website_url" provider={provider} ds={ds}>
        <TextField label="Website / Bio URL" value={provider.website_url ?? ''} onChange={u('website_url')} size="small" fullWidth />
      </HL>
    </Grid>
  </>;
}

/* ─── Section 2: Clinical & Credentials ─── */

export function ClinicalSection({ provider, idx, ds, onUpdate, roleOptions, licenseTypes, degreeTypes }: SectionProps) {
  const u = useFieldChange(idx, onUpdate);
  const allRoles = useMemo(
    () => provider.roles?.length ? provider.roles : provider.role ? [provider.role] : [],
    [provider.roles, provider.role],
  );
  const hasBakedIn = allRoles.some((r) => BILLING_BAKED_IN_ROLES.has(r));
  const hasEligible = allRoles.some((r) => BILLING_TIER_ELIGIBLE_ROLES_SET.has(r));
  const licenses = useMemo(() => provider.licenses ?? [], [provider.licenses]);

  const addLicense = useCallback(() => {
    onUpdate(idx, 'licenses', [...licenses, { license_type: '', license_number: '', license_state: '', license_expiration_date: '' }]);
  }, [idx, licenses, onUpdate]);

  const updateLicense = useCallback((li: number, field: keyof LicenseEntry, value: string | boolean) => {
    onUpdate(idx, 'licenses', licenses.map((l, i) => (i === li ? { ...l, [field]: value } : l)));
  }, [idx, licenses, onUpdate]);

  const removeLicense = useCallback((li: number) => {
    onUpdate(idx, 'licenses', licenses.filter((_, i) => i !== li));
  }, [idx, licenses, onUpdate]);

  return <>
    <Grid size={{ xs: 12, sm: 6 }}>
      <HL field="roles" provider={provider} ds={ds}>
        <Autocomplete multiple size="small" options={roleOptions}
          getOptionLabel={(opt) => opt.label}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          value={roleOptions.filter((opt) => allRoles.includes(opt.value))}
          onChange={(_e, nv) => onUpdate(idx, 'roles', nv.map((v) => v.value))}
          renderTags={(value, getTagProps) =>
            value.map((option, i) => (
              <Chip {...getTagProps({ index: i })} key={option.value} label={option.label} size="small"
                color={option.is_clinical ? 'primary' : 'info'} variant="outlined" />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Roles" placeholder="Select roles…" />}
        />
      </HL>
    </Grid>
    {hasBakedIn ? (
      <Grid size={{ xs: 12, sm: 6 }}>
        <Typography variant="caption" color="success.main" sx={{ fontStyle: 'italic', mt: 1, display: 'block' }}>
          Full billing access included with {allRoles.filter((r) => BILLING_BAKED_IN_ROLES.has(r)).join(', ')} role
        </Typography>
      </Grid>
    ) : hasEligible ? (
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField select label="Billing Access" value={provider.billing_tier ?? 'none'}
          onChange={(e) => onUpdate(idx, 'billing_tier', e.target.value)} size="small" fullWidth
          helperText={BILLING_TIER_OPTIONS.find((o) => o.value === (provider.billing_tier ?? 'none'))?.description}>
          {BILLING_TIER_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
        </TextField>
      </Grid>
    ) : <Grid size={{ xs: 12, sm: 6 }} />}

    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField label="NPI Number" value={provider.npi_number} onChange={u('npi_number')} size="small" fullWidth
        placeholder="10-digit NPI" inputProps={{ maxLength: 10 }} />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <TaxonomyCodeField value={provider.taxonomy_code ?? ''} onChange={(v) => onUpdate(idx, 'taxonomy_code', v)} />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <FormControlLabel label="Can Prescribe" control={
        <Checkbox size="small" checked={provider.can_prescribe ?? false}
          onChange={(e) => onUpdate(idx, 'can_prescribe', e.target.checked)} sx={{ p: 0.5 }} />
      } sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }} />
    </Grid>
    {provider.can_prescribe && (
      <>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="DEA Number" value={provider.dea_number ?? ''} onChange={u('dea_number')} size="small" fullWidth
            helperText="Required only if prescribing controlled substances" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="Rx Authority #" value={provider.prescription_authority_number ?? ''}
            onChange={u('prescription_authority_number')} size="small" fullWidth />
        </Grid>
      </>
    )}

    {/* Licenses (repeatable) */}
    <Grid size={12}>
      <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>Licenses</Typography>
      {licenses.map((lic, li) => (
        <Box key={li} sx={{ display: 'flex', gap: 0.5, mb: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete freeSolo size="small" options={licenseTypes} value={lic.license_type}
            getOptionLabel={formatLicenseTypeLabel}
            onChange={(_e, v) => updateLicense(li, 'license_type', (v ?? '') as string)}
            onInputChange={(_e, v) => updateLicense(li, 'license_type', v)}
            renderInput={(params) => <TextField {...params} label="Type" sx={{ minWidth: 100 }} />}
            sx={{ flex: 1, minWidth: 100 }}
          />
          <TextField label="Number" value={lic.license_number ?? ''} size="small" sx={{ flex: 1, minWidth: 80 }}
            onChange={(e) => updateLicense(li, 'license_number', e.target.value)} />
          <TextField select label="State" value={lic.license_state ?? ''} size="small" sx={{ minWidth: 72 }}
            onChange={(e) => updateLicense(li, 'license_state', e.target.value)}>
            <MenuItem value="">—</MenuItem>
            {US_STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <IsoDateField label="Exp Date" value={lic.license_expiration_date ?? ''} size="small"
            sx={{ minWidth: 130 }}
            onChange={(v) => updateLicense(li, 'license_expiration_date', v)} />
          <Tooltip title="Mark as primary license for merge fields">
            <FormControlLabel
              control={
                <Checkbox size="small" checked={lic.is_primary ?? false}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onUpdate(idx, 'licenses', licenses.map((l, i) => ({ ...l, is_primary: i === li })));
                    } else {
                      updateLicense(li, 'is_primary', false);
                    }
                  }}
                  sx={{ p: 0.5 }} />
              }
              label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Primary</Typography>}
              sx={{ mx: 0, minWidth: 'auto' }}
            />
          </Tooltip>
          <IconButton size="small" onClick={() => removeLicense(li)} color="error" aria-label="Delete"><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>
      ))}
      <IconButton size="small" onClick={addLicense} color="primary" sx={{ mt: 0.5 }} aria-label="Add License">
        <AddIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ ml: 0.25 }}>Add License</Typography>
      </IconButton>
    </Grid>

    <Grid size={{ xs: 12, sm: 6 }}>
      <HL field="degrees" provider={provider} ds={ds}>
        <Autocomplete multiple freeSolo size="small" options={degreeTypes}
          value={Array.isArray(provider.degrees) ? provider.degrees : []}
          onChange={(_e, v) => onUpdate(idx, 'degrees', v)}
          renderTags={(value, getTagProps) =>
            value.map((option, i) => <Chip {...getTagProps({ index: i })} key={option} label={option} size="small" />)
          }
          renderInput={(params) => <TextField {...params} label="Degrees" placeholder="Add…" />}
        />
      </HL>
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <TextField label="Supervisor" value={provider.supervisor_name ?? ''} onChange={u('supervisor_name')} size="small" fullWidth
        helperText="Supervising clinician (if applicable)" />
    </Grid>
    <Grid size={12}>
      <FormControlLabel label="Requires Supervision" control={
        <Checkbox size="small" checked={provider.requires_supervision ?? false}
          onChange={(e) => onUpdate(idx, 'requires_supervision', e.target.checked)} sx={{ p: 0.5 }} />
      } sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }} />
    </Grid>
  </>;
}

/* ─── Section 3: Bio & Practice ─── */

export function BioSection({ provider, idx, ds, onUpdate }: SectionProps) {
  const u = useFieldChange(idx, onUpdate);
  return <>
    <Grid size={12}>
      <HL field="bio" provider={provider} ds={ds}>
        <TextField label="Bio" value={provider.bio ?? ''} onChange={u('bio')} size="small" fullWidth
          multiline minRows={2} maxRows={6} placeholder="Short professional bio…" />
      </HL>
    </Grid>
    <Grid size={12}>
      <HL field="specialties" provider={provider} ds={ds}>
        <Autocomplete multiple freeSolo size="small" options={[]}
          value={Array.isArray(provider.specialties) ? provider.specialties : []}
          onChange={(_e, v) => onUpdate(idx, 'specialties', v)}
          renderTags={(value, getTagProps) =>
            value.map((option, i) => <Chip {...getTagProps({ index: i })} key={option} label={option} size="small" />)
          }
          renderInput={(params) => <TextField {...params} label="Specialties" placeholder="Type and press Enter…" />}
        />
      </HL>
    </Grid>
    <Grid size={12}>
      <TextField label="Educational Information" value={provider.educational_information ?? ''} onChange={u('educational_information')}
        size="small" fullWidth multiline minRows={1} maxRows={3} />
    </Grid>
    <Grid size={12}>
      <TextField label="Disclosure Blurb" value={provider.disclosure_blurb ?? ''} onChange={u('disclosure_blurb')}
        size="small" fullWidth multiline minRows={1} maxRows={3}
        helperText="Shown on client-facing materials (e.g. portal profile)" />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <HL field="age_range" provider={provider} ds={ds}>
        <TextField label="Age Range" value={provider.age_range ?? ''} onChange={u('age_range')}
          size="small" fullWidth placeholder='e.g. "Adults (18+)"' />
      </HL>
    </Grid>
    <Grid size={{ xs: 6, sm: 4 }}>
      <TextField label="Min Client Age" type="number" value={provider.min_client_age ?? ''} size="small" fullWidth
        onChange={(e) => onUpdate(idx, 'min_client_age', e.target.value ? Number(e.target.value) : null)} />
    </Grid>
    <Grid size={{ xs: 6, sm: 4 }}>
      <TextField label="Max Client Age" type="number" value={provider.max_client_age ?? ''} size="small" fullWidth
        onChange={(e) => onUpdate(idx, 'max_client_age', e.target.value ? Number(e.target.value) : null)} />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <FormControlLabel label="Accepting New Clients" control={
        <Checkbox size="small" checked={provider.accepting_new_clients ?? true}
          onChange={(e) => onUpdate(idx, 'accepting_new_clients', e.target.checked)} sx={{ p: 0.5 }} />
      } sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }} />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField label="Capacity Notes" value={provider.capacity_notes ?? ''} onChange={u('capacity_notes')} size="small" fullWidth />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField label="New Client Openings" type="number" value={provider.new_client_openings ?? ''} size="small" fullWidth
        onChange={(e) => onUpdate(idx, 'new_client_openings', e.target.value ? Number(e.target.value) : null)} />
    </Grid>
  </>;
}

/* ─── Section 4: Scheduling ─── */

export function SchedulingSection({ provider, idx, onUpdate }: SectionProps) {
  return <>
    {(provider.suggested_appointment_types?.length ?? 0) > 0 && (
      <Grid size={12}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Suggested Appointment Types (from crawl)
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
          {(provider.suggested_appointment_types ?? []).map((t) => (
            <Chip key={t} label={t} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.75rem' }} />
          ))}
        </Box>
      </Grid>
    )}
    <Grid size={{ xs: 12, sm: 4 }}>
      <FormControlLabel label="Self-Scheduling Opt-Out" control={
        <Checkbox size="small" checked={provider.self_scheduling_opt_out ?? false}
          onChange={(e) => onUpdate(idx, 'self_scheduling_opt_out', e.target.checked)} sx={{ p: 0.5 }} />
      } sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }} />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <FormControlLabel label="Can Self-Schedule via Online Widget (New Clients)" control={
        <Checkbox size="small" checked={provider.accepting_new_client_online_self_bookings ?? true}
          onChange={(e) => onUpdate(idx, 'accepting_new_client_online_self_bookings', e.target.checked)} sx={{ p: 0.5 }} />
      } sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }} />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <FormControlLabel label="Can Self-Schedule via Client Portal (Existing Clients)" control={
        <Checkbox size="small" checked={provider.accepting_existing_client_online_self_bookings ?? true}
          onChange={(e) => onUpdate(idx, 'accepting_existing_client_online_self_bookings', e.target.checked)} sx={{ p: 0.5 }} />
      } sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }} />
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <TextField label="Target Billable Clinical Hours / Week" type="number" value={provider.goal_sessions_per_week ?? ''} size="small" fullWidth
        onChange={(e) => onUpdate(idx, 'goal_sessions_per_week', e.target.value ? Number(e.target.value) : null)} />
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <TextField label="Max Sessions / Week" type="number" value={provider.max_sessions_per_week ?? ''} size="small" fullWidth
        onChange={(e) => onUpdate(idx, 'max_sessions_per_week', e.target.value ? Number(e.target.value) : null)} />
    </Grid>
  </>;
}

/* ─── Section 5: Billing & Pay ─── */

export function BillingSection({ provider, idx, onUpdate, payTypes, timesheetModes, showPayType }: SectionProps) {
  const u = useFieldChange(idx, onUpdate);
  return <>
    {showPayType && (
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField select label="Pay Type" value={provider.pay_type} onChange={u('pay_type')} size="small" fullWidth>
          {payTypes.map((pt) => <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>)}
        </TextField>
      </Grid>
    )}
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField select label="Timesheet Mode" value={provider.timesheet_mode} onChange={u('timesheet_mode')} size="small" fullWidth>
        {timesheetModes.map((tm) => <MenuItem key={tm.value} value={tm.value}>{tm.label}</MenuItem>)}
      </TextField>
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <FormControlLabel label="Incident-to Billing" control={
        <Checkbox size="small" checked={provider.use_incident_to_billing ?? false}
          onChange={(e) => onUpdate(idx, 'use_incident_to_billing', e.target.checked)} sx={{ p: 0.5 }} />
      } sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }} />
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <TextField select label="Superbill NPI Preference" value={provider.superbill_npi_preference ?? ''}
        onChange={u('superbill_npi_preference')} size="small" fullWidth>
        {SUPERBILL_NPI_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    </Grid>
    <Grid size={12}>
      <TextField label="Fee Notes" value={provider.fee_notes ?? ''} onChange={u('fee_notes')} size="small" fullWidth
        multiline minRows={1} maxRows={3} helperText="Reference notes about this provider's fee schedule" />
    </Grid>
  </>;
}

/* ─── Section 6: Telehealth ─── */

export function TelehealthSection({ provider, idx, onUpdate }: SectionProps) {
  const u = useFieldChange(idx, onUpdate);
  return <>
    <Grid size={{ xs: 12, sm: 6 }}>
      <TextField label="Telehealth URL" value={provider.telehealth_url ?? ''} onChange={u('telehealth_url')} size="small" fullWidth
        placeholder="https://zoom.us/j/..." />
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <TextField select label="Telehealth Mode" value={provider.telehealth_mode_override ?? ''}
        onChange={u('telehealth_mode_override')} size="small" fullWidth>
        {TELEHEALTH_MODE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    </Grid>

    <Grid size={12}>
      <TextField label="Telehealth Instructions" value={provider.telehealth_instructions ?? ''}
        onChange={u('telehealth_instructions')} size="small" fullWidth multiline minRows={1} maxRows={3}
        placeholder="Instructions sent to clients before telehealth sessions" />
    </Grid>
  </>;
}

/* ─── Section 7: Address ─── */

export function AddressSection({ provider, idx, onUpdate }: SectionProps) {
  const u = useFieldChange(idx, onUpdate);
  return <>
    <Grid size={12}>
      <TextField label="Address Line 1" value={provider.address_line1 ?? ''} onChange={u('address_line1')} size="small" fullWidth />
    </Grid>
    <Grid size={12}>
      <TextField label="Address Line 2" value={provider.address_line2 ?? ''} onChange={u('address_line2')} size="small" fullWidth />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField label="City" value={provider.city ?? ''} onChange={u('city')} size="small" fullWidth />
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField select label="State" value={provider.state ?? ''} onChange={u('state')} size="small" fullWidth>
        <MenuItem value="">—</MenuItem>
        {US_STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
      </TextField>
    </Grid>
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField label="ZIP Code" value={provider.postal_code ?? ''} onChange={u('postal_code')} size="small" fullWidth />
    </Grid>
  </>;
}
